import { useMutation } from '@apollo/client';
import type { InsertRiskInternalAuditResultMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertRiskInternalAuditResultDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertRiskInternalAuditResultInput } from './useInsertRiskInternalAuditResultTRPC';
import { useInsertRiskInternalAuditResultTRPC } from './useInsertRiskInternalAuditResultTRPC';

export const useInsertRiskInternalAuditResult = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [insertGraphQL, graphqlState] = useMutation(
    InsertRiskInternalAuditResultDocument,
    {
      update: (cache) => {
        evictField(cache, 'risk_controlled_internal_audit_result');
        evictField(cache, 'risk_uncontrolled_internal_audit_result');
        evictField(cache, 'internal_audit_report');
        evictField(cache, 'risk_controlled_internal_audit_result_aggregate');
        evictField(cache, 'risk_uncontrolled_internal_audit_result_aggregate');
      },
    }
  );

  const trpcMutation = useInsertRiskInternalAuditResultTRPC();

  const insertRiskInternalAuditResult = async (
    variables: InsertRiskInternalAuditResultInput
  ): Promise<InsertRiskInternalAuditResultMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertRiskInternalAuditResult(variables);
    }

    const result = await insertGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to insert risk internal audit result');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertRiskInternalAuditResult,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertRiskInternalAuditResult,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
